// src/components/CartModal.tsx
"use client";

import React, { useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { useCartStore } from "@/stores/cartStore";
import { useWebSocketStore } from "@/stores/websocketStore";
import "@/components/global/css/CartModal.css";

// Define the CartItem type (adjust based on your actual data structure)
interface CartItem {
  id: number;
  media_id: number;
  quantity: number;
  media: {
    display_name: string;
    market_price: number;
    img_final?: string;
    hyperlink?: string;
    url?: string;
  };
  project?: { name: string };
}

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartModal: React.FC<CartModalProps> = ({ isOpen, onClose }) => {
  const { data: session, status } = useSession();
  const {
    guestCart,
    loggedInCart,
    removeFromCart,
    fetchCart,
    applyPromoCode,
    removePromoCode,
    promoCode,
    promoError,
    isApplying,
    setPromoCode,
    setPromoError,
  } = useCartStore();

  // Select the appropriate cart based on authentication status
  const cart = status === "authenticated" ? loggedInCart : guestCart;
  const cartItems = cart.cartItems;
  const subtotal = cart.subtotal;
  const total = cart.total;
  const promo = cart.promo;
  const discount = promo ? promo.discount_applied : 0;

  // Calculate quantities with explicit types
  const quantities: { [key: number]: number } = cartItems
    ? cartItems.reduce(
        (acc: { [key: number]: number }, item: CartItem) => ({
          ...acc,
          [item.id]: item.quantity,
        }),
        {}
      )
    : {};

  useEffect(() => {
    if (isOpen && status === "authenticated" && session?.user?.id) {
      document.body.style.overflow = "hidden";
      const userId = session.user.id.toString();
      fetchCart(userId);

      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      if (!apiUrl) {
        console.error("NEXT_PUBLIC_API_URL is not defined in environment variables");
        return;
      }
      const wsUrl = apiUrl.replace(/^https?:\/\//, "wss://") + "/media-updates";
      const ws = useWebSocketStore.getState().connections.get(wsUrl);
      if (ws) {
        const handleMessage = async (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          if (data.type === "cart_updated") {
            await fetchCart(userId);
          } else if (data.type === "media_updated") {
            const updatedMedia = data.payload;
            useCartStore.setState((state) => {
              const exists = state.loggedInCart.cartItems.some(
                (item: CartItem) => item.media_id === updatedMedia.id
              );
              if (exists) {
                const updatedCartItems = state.loggedInCart.cartItems.map((item: CartItem) =>
                  item.media_id === updatedMedia.id
                    ? { ...item, media: { ...item.media, ...updatedMedia } }
                    : item
                );
                const subtotal = updatedCartItems.reduce(
                  (sum: number, item: CartItem) => sum + item.media.market_price * item.quantity,
                  0
                );
                const discount = state.loggedInCart.promo
                  ? state.loggedInCart.promo.discount_applied
                  : 0;
                return {
                  loggedInCart: {
                    ...state.loggedInCart,
                    cartItems: updatedCartItems,
                    subtotal,
                    total: subtotal - discount,
                  },
                };
              }
              return state;
            });
          } else if (data.type === "media_removed") {
            const removedMediaId = data.payload.id;
            useCartStore.setState((state) => {
              const updatedCartItems = state.loggedInCart.cartItems.filter(
                (item: CartItem) => item.media_id !== removedMediaId
              );
              const subtotal = updatedCartItems.reduce(
                (sum: number, item: CartItem) => sum + item.media.market_price * item.quantity,
                0
              );
              const discount = state.loggedInCart.promo
                ? state.loggedInCart.promo.discount_applied
                : 0;
              return {
                loggedInCart: {
                  ...state.loggedInCart,
                  cartItems: updatedCartItems,
                  subtotal,
                  total: subtotal - discount,
                },
              };
            });
          }
        };

        ws.addEventListener("message", handleMessage);
        return () => {
          ws.removeEventListener("message", handleMessage);
          document.body.style.overflow = "auto";
        };
      }
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isOpen, session, status, fetchCart]);

  useEffect(() => {
    if (isOpen && promo && promo.code !== promoCode) {
      setPromoCode(promo.code);
    }
  }, [isOpen, promo, promoCode, setPromoCode]);

  useEffect(() => {
    if (promoError) {
      const timer = setTimeout(() => setPromoError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [promoError, setPromoError]);

  const handleApplyPromo = async () => {
    if (!promoCode) return;
    try {
      await applyPromoCode(promoCode);
    } catch {
      // Error handled in store
    }
  };

  const handleRemovePromo = async () => {
    try {
      await removePromoCode();
    } catch {
      // Error handled in store
    }
  };

  const handleRemoveItem = async (cartItemId: number) => {
    await removeFromCart(cartItemId);
  };

  const getItemDiscountedPrice = (item: CartItem) => {
    if (!promo) return null;

    const originalPrice = item.media.market_price * (quantities[item.id] || item.quantity);
    let discountedPrice = originalPrice;

    const isPercentDiscount = promo.discount_type === "percent";
    const limitedProducts = promo.limit_products ? promo.limit_products.split(",").map(Number) : null;
    const isEligible = limitedProducts ? limitedProducts.includes(item.media_id) : true;

    if (isPercentDiscount && isEligible) {
      const discountRate = promo.discount_value / 100;
      discountedPrice = originalPrice * (1 - discountRate);
      return discountedPrice;
    } else if (!isPercentDiscount && isEligible && limitedProducts) {
      const eligibleItemsCount =
        cartItems?.filter((i: CartItem) => limitedProducts.includes(i.media_id)).length || 0;
      const discountPerItem = promo.discount_value / eligibleItemsCount;
      discountedPrice = Math.max(0, originalPrice - discountPerItem);
      return discountedPrice;
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="cart-modal-overlay" onClick={onClose}>
      <div className="cart-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cart-modal-close" onClick={onClose}>
          ✕
        </button>
        <div className="cart-section">
          <div className="container">
            <form className="cart-list-form" onSubmit={(e) => e.preventDefault()}>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th colSpan={2}>Product</th>
                      <th className="prc">Price</th>
                      <th className="qty">QTY</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems?.map((item: CartItem) => {
                      const originalTotal = item.media.market_price * (quantities[item.id] || item.quantity);
                      const discountedPrice = getItemDiscountedPrice(item);

                      return (
                        <tr key={item.id}>
                          <td className="product-thumbnails">
                            <Link
                              href={`/marketplace?search=${encodeURIComponent(item.media.display_name)}&id=${item.media_id}`}
                              className="product-img"
                            >
                              <Image
                                src={`/images/media-logos/${item.media.img_final || "media_default_img-2.webp"}`}
                                alt={item.media.display_name}
                                width={80}
                                height={80}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = "/images/media-logos/media_default_img-2.webp";
                                }}
                              />
                            </Link>
                          </td>
                          <td className="product-info">
                            <Link
                              href={`/marketplace?search=${encodeURIComponent(item.media.display_name)}&id=${item.media_id}`}
                              className="product-name"
                            >
                              {item.media.display_name}
                            </Link>
                            <div className="serial">
                              <Link href={item.media.hyperlink || "#"} target="_blank">
                                {item.media.url || "no website"}{" "}
                                <Image
                                  src="/images/icon/external-link.svg"
                                  width={15}
                                  height={15}
                                  alt="External link"
                                />
                              </Link>
                            </div>
                            {session?.user?.id && item.project?.name && (
                              <div className="project">
                                <Image
                                  src="/images/icon/target.svg"
                                  width={25}
                                  height={25}
                                  alt="Target project"
                                />
                                {item.project.name}
                              </div>
                            )}
                          </td>
                          <td className="price prc">
                            <span>
                              ${item.media.market_price.toLocaleString("en-US", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </td>
                          <td className="quantity">
                            <ul className="order-box style-none">
                              <li>
                                <button className="btn value-decrease" disabled>-</button>
                              </li>
                              <li>
                                <input
                                  type="number"
                                  min="1"
                                  value={quantities[item.id] || item.quantity}
                                  disabled
                                  className="product-value val"
                                />
                              </li>
                              <li>
                                <button className="btn value-increase" disabled>+</button>
                              </li>
                            </ul>
                          </td>
                          <td className="price total-price">
                            {discountedPrice !== null ? (
                              <>
                                <span className="original-price">
                                  ${originalTotal.toLocaleString("en-US", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                                <span className="discounted-price">
                                  ${discountedPrice.toLocaleString("en-US", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </>
                            ) : (
                              <span>
                                ${originalTotal.toLocaleString("en-US", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            )}
                          </td>
                          <td>
                            <button className="remove-product" onClick={() => handleRemoveItem(item.id)}>
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    }) || null}
                  </tbody>
                </table>
              </div>
              <div className="d-sm-flex justify-content-between cart-footer">
                <div className="coupon-section d-flex flex-column">
                  <div className="coupon-form d-lg-flex align-items-center">
                    <input
                      type="text"
                      placeholder="Enter your code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      disabled={promo !== null || isApplying}
                    />
                    {promo ? (
                      <button className="theme-btn-seven md-mt-20 sm-mb-20 ml-10" onClick={handleRemovePromo}>
                        Remove
                      </button>
                    ) : (
                      <button
                        className="theme-btn-seven md-mt-20 sm-mb-20 cart-promo-button"
                        onClick={handleApplyPromo}
                        disabled={!promoCode || isApplying}
                      >
                        {isApplying ? <span className="cart-promo-loader"></span> : "Apply Coupon"}
                      </button>
                    )}
                  </div>
                  {promo && (
                    <div className="promo-messages">
                      <div className="promo-success mt-10">{promo.success_message}</div>
                      {promo.note && <div className="promo-note mt-5">{promo.note}</div>}
                    </div>
                  )}
                  {promoError && <div className="promo-error mt-10">{promoError}</div>}
                </div>
                <div className="cart-total-section d-flex flex-column sm-pt-40 align-items-end">
                  <table className="cart-total-table">
                    <tbody>
                      {promo ? (
                        <>
                          <tr>
                            <th>Subtotal:</th>
                            <td>
                              ${(subtotal ?? 0).toLocaleString("en-US", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                          <tr>
                            <th>Discount:</th>
                            <td>
                              -${(discount ?? 0).toLocaleString("en-US", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                          <tr>
                            <th>Total:</th>
                            <td>
                              ${(total ?? 0).toLocaleString("en-US", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })}
                              <span>USD</span>
                            </td>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <th>Total:</th>
                          <td>
                            ${(total ?? 0).toLocaleString("en-US", {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            })}
                            <span>USD</span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="checkout-btns">
                    <button onClick={onClose} className="checkout-btn white mt-30">
                      Go back
                    </button>
                    <Link href="/checkout" className="checkout-btn black checkout-process mt-30">
                      Checkout
                    </Link>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartModal;